import {AppUrls} from 'shared/config/model/urls';

import {MouseEvent, useCallback} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';

import {MonitorRowClickHandler, useMonitorRowSelection, useMonitorRowState} from 'entities/monitor';
import {
    getServerRowIconState,
    getServerRowSeverity,
    IServerRowIconState,
    ServerModalType,
} from 'features/server/lib/serverRowView';
import {isServerPaused} from 'features/server/lib/serverStatusHelper';
import {getServerDateFormat} from 'features/server/lib/serverView';
import useToggleTagFilter from 'shared/lib/hooks/useToggleTagFilter';
import {isTranslate} from 'shared/lib/lang';

import {ServerAlertRuleSeverity} from './ServerAlertRule/IServerAlertRule';
import IServerOutput from './Server/IServer';
import {serverSelectedIdsSelector, toggleOne} from './serverSlice';
import useServerToggle from './useServerToggle';

export interface IUseServerRow {
    activeModal: ServerModalType,
    closeModal: () => void,
    isSelected: boolean,
    isPaused: boolean,
    iconState: IServerRowIconState,
    severity: ServerAlertRuleSeverity | null,
    dateFormat: string,
    tags: NonNullable<IServerOutput['tags']>,
    onCheckboxChange: () => void,
    onTagClick: (_tag: string) => void,
    onToggleClick: (_event?: MouseEvent) => Promise<void>,
    onDetailsClick: MonitorRowClickHandler,
    onEditClick: MonitorRowClickHandler,
    onTagsClick: MonitorRowClickHandler,
    onPauseClick: MonitorRowClickHandler,
    onResumeClick: MonitorRowClickHandler,
    onSetTokenClick: MonitorRowClickHandler,
    onRegenerateTokenClick: MonitorRowClickHandler,
    onDeleteClick: MonitorRowClickHandler,
}

export const useServerRow = (item: IServerOutput): IUseServerRow => {
    const {activeModal, setActiveModal, closeModal, openModal} = useMonitorRowState<ServerModalType>();
    const {isSelected, onCheckboxChange} = useMonitorRowSelection(item.uuid, serverSelectedIdsSelector, toggleOne);
    const navigate = useNavigate();
    const onTagClick = useToggleTagFilter();
    const onToggleClick = useServerToggle(item, useCallback(() => setActiveModal('limit'), [setActiveModal]));

    const onDetailsClick: MonitorRowClickHandler = (event) => {
        event.stopPropagation();
        navigate(generatePath(AppUrls.SERVER_DETAILS, {serverId: item.uuid}));
    };

    return {
        activeModal,
        closeModal,
        isSelected,
        isPaused: isServerPaused(item.status),
        iconState: getServerRowIconState(item.status),
        severity: getServerRowSeverity(item.incidents_severity),
        dateFormat: getServerDateFormat(isTranslate()),
        tags: item.tags ?? [],
        onCheckboxChange,
        onTagClick,
        onToggleClick,
        onDetailsClick,
        onEditClick: openModal('edit'),
        onTagsClick: openModal('tags'),
        onPauseClick: openModal('pause'),
        onResumeClick: openModal('resume'),
        onSetTokenClick: openModal('setToken'),
        onRegenerateTokenClick: openModal('regenerate'),
        onDeleteClick: openModal('delete'),
    };
};

export default useServerRow;
